<script>
  import { afterUpdate, onMount } from "svelte";
  import RadioWithSelect from "./Filters/RadioWithSelect.svelte";
  import RadioNoInput from "./Filters/RadioNoInput.svelte";
  import SimpleInputs from "./Filters/SimpleInputs.svelte";
  import DateInput from "./Filters/DateInput.svelte";
  import { fixedFilters, ORDER_FILTER_TYPE } from "./static";
  import FilterRow from "./FilterRow.svelte";
  import {ignore, makeWebsiteRequest} from "../Common/tools";
  import {External, M} from "../external";

  export let filters,
    enableOR = false,
    enableSaveSegment = true,
    enableHide = true,
    addNonPropertyFilters = true,
    onChange,
    onSaveSegment = ignore,
    onUpdateSegment = ignore,
    id = null,
    hide = false,
    customFilters = [],
    disabledCrmProperties = [];

  let renderFilters = [[]],
    subscriberLists = [],
    currentFilterRow = 0,
    showSaveSegment = false,
    hideFilters = false,
    showUpdate = false;

  const funcNames = {
    is: M.IS,
    isNot: M.IS_NOT,
    startsWith: M.STARTS_WITH,
    endsWith: M.ENDS_WITH,
    contains: M.CONTAINS,
    containsNot: M.DOESNT_CONTAIN,
    greater: M.GRATER_THAN,
    less: M.LESS_THAN,
    isTrue: M.IS_TRUE,
    isFalse: M.IS_FALSE,
    moreThan: M.CRM_MORE_THAN_DAYS_LEFT,
    exactly: M.CRM_EXACTLY_DAYS_LEFT,
    lessThan: M.CRM_LESS_THAN,
    after: M.AFTER,
    before: M.BEFORE,
    unknown: M.IS_UNKNOWN,
    any: M.HAS_ANY_VALUE
  };

  export function onFilterChanged() {
    onFilterChangedDebounce();
  }

  export function isEmpty() {
    for (let i = 0; i < filters.length; i++) {
      if (filters[i].length !== 0) {
        return false;
      }
    }
    return true;
  }

  function isListEmpty(list) {
    for (let i = 0; i < list.length; i++) {
      if (list[i].length !== 0) {
        return false;
      }
    }
    return true;
  }

  function onAddFilterClick(e, idx) {
    if (!isLastFilterValid()) {
      External.Tools.showError(M.COMPLETE_FILTER);
      return;
    }
    let fixedFiltersAdded = false;
    let items = [];

    if (customFilters && customFilters.length) {
      items.push(...customFilters);
    }

    External.Website.crmProperties.forEach(function (property) {
      if (disabledCrmProperties.indexOf(property.id) > -1 || disabledCrmProperties.indexOf(property.name) > -1) {
        return;
      }
      items.push(
        External.$.extend({}, property, {
          icon: External.getIconForProperty(property),
          fType: External.CRM_FILTER_TYPE.PROPERTY
        })
      );
      if (property.id === -3) {
        // add all fixed filters after the predefined properties
        fixedFilters.forEach(function (filter) {
          if (!addNonPropertyFilters && (filter.fType === External.CRM_FILTER_TYPE.TAG || filter.fType === External.CRM_FILTER_TYPE.SUBSCRIBER_LIST)) {
            return;
          }
          items.push(filter);
        });
        fixedFiltersAdded = true;
      }
    });

    if (!fixedFiltersAdded) {
      fixedFilters.forEach(function (filter) {
        if (!addNonPropertyFilters && (filter.fType === External.CRM_FILTER_TYPE.TAG || filter.fType === External.CRM_FILTER_TYPE.SUBSCRIBER_LIST)) {
          return;
        }
        items.push(filter);
      });
    }

    const target = External.$(e.target ? e.target : e);
    External.Tools.showFloatingPopupList({
      target: target,
      items: items,
      onSelect: function (selected) {
        let newFilter = {
          func: null,
          type: selected.fType,
          id: selected.id,
          isAlwaysDefined: selected.isAlwaysDefined,
          value: null
        };

        if (!filters[idx]) {
          filters[idx] = [];
        }

        filters[idx].push(newFilter);
        filters = Object.assign([], filters, []);
        renderFilter(newFilter, true, idx, target);
      }
    });
  }

  function addOr(e) {
    filters.push([]);
    let idx = renderFilters.push([]);
    renderFilters = Object.assign([], renderFilters, []);
    filters = Object.assign([], filters, []);
    onAddFilterClick(e, idx - 1);
  }

  function isLastFilterValid() {
    if (filters.length === 0) {
      showSaveSegment = false;
      return true;
    }

    for (let i = 0; i < filters.length; i++) {
      const filterRow = filters[i];

      if (filterRow.length === 0) {
        continue;
      }

      const filter = filterRow[filterRow.length - 1];

      if (!isFilterValid(filter)) {
        return false;
      }
    }

    showSaveSegment = true;
    return true;
  }

  function isFilterValid(filter) {
    if (!filter.func) return false;
    if (filter.func === "any" || filter.func === "unknown" || filter.func === "isTrue" || filter.func === "isFalse") return true;
    return !!filter.value;
  }

  function getPropertyByFilter(filter) {
    if (filter.type === External.CRM_FILTER_TYPE.PROPERTY) {
      return External.getById(External.Website.crmProperties, filter.id);
    }

    for (let idx = 0; idx < fixedFilters.length; ++idx) {
      if (fixedFilters[idx].fType === filter.type) return fixedFilters[idx];
    }

    for (let idx = 0; idx < customFilters.length; ++idx) {
      if (customFilters[idx].fType === filter.type && customFilters[idx].id === filter.id) {
        return customFilters[idx];
      }
    }

    return null;
  }

  function renderFilter(filter, edit, idx, target) {
    let property = getPropertyByFilter(filter);
    if (property === null) return false;
    let func = "";
    let value = "...";
    let suffix = "";

    if (filter.func) {
      func = funcNames[filter.func];
    }

    let isWithoutValue = filter.func === "any" || filter.func === "unknown";

    if (isWithoutValue || filter.func === "isTrue" || filter.func === "isFalse") value = "";
    else if (filter.value !== null && filter.value !== "") value = filter.value;

    if (filter.value && property.type === External.CRM_PROPERTY_TYPE.COUNTRY) value = External.COUNTRIES[External.COUNTRY_CODES.indexOf(filter.value)] || filter.value;

    let isDaysAgo = filter.func === "moreThan" || filter.func === "exactly" || filter.func === "lessThan";
    if (value && value !== "..." && isDaysAgo) suffix = M.CRM_DAYS_AGO_RIGHT;

    if (filter.value && property.type === External.CRM_PROPERTY_TYPE.DATE && !isWithoutValue && !isDaysAgo) {
      value = External.Tools.dateToShortString(new Date(+filter.value), true, true);
    }

    if (!renderFilters[idx]) {
      renderFilters[idx] = [];
    }

    renderFilters[idx].push({
      name: property.label,
      filter: func,
      value: value,
      suffix: suffix
    });

    renderFilters = Object.assign([], renderFilters, []);

    if (edit) {
      editFilterValue(target, filter);
      currentFilterRow = idx;
    }
  }

  function editFilterValue(button, filter) {
    let property = getPropertyByFilter(filter);
    if (filter.type === External.CRM_FILTER_TYPE.TAG) {
      editFilterTag(button, filter, property);
    } else if (filter.type === External.CRM_FILTER_TYPE.MEMBER_GROUP) {
      editMemberGroup(button, filter, property);
    } else if (filter.type === External.CRM_FILTER_TYPE.SUBSCRIBER_LIST) {
      editSubscriberList(button, filter, property);
    } else if (filter.type === External.CRM_FILTER_TYPE.ORDER_PROPERTY && property.type === External.CRM_PROPERTY_TYPE.LIST) {
      switch (filter.id) {
        case ORDER_FILTER_TYPE.STATUS:
          editOrderStatusFilter(button, filter, property);
          break;
        case ORDER_FILTER_TYPE.FUNNEL:
          editFunnelFilter(button, filter, property);
          break;
        case ORDER_FILTER_TYPE.PRODUCT:
          editProductFilter(button, filter, property);
          break;
      }
    } else {
      switch (property.type) {
        case External.CRM_PROPERTY_TYPE.STRING:
        case External.CRM_PROPERTY_TYPE.EMAIL:
          editFilterWithSimpleInputs(button, filter, "text", ["is", "isNot", "startsWith", "endsWith", "contains", "containsNot"]);
          break;
        case External.CRM_PROPERTY_TYPE.NUMBER:
          editFilterWithSimpleInputs(button, filter, "number", ["is", "isNot", "greater", "less"]);
          break;
        case External.CRM_PROPERTY_TYPE.BOOLEAN:
          editFilterBool(button, filter, property);
          break;
        case External.CRM_PROPERTY_TYPE.DATE:
          editFilterDate(button, filter, property);
          break;
        case External.CRM_PROPERTY_TYPE.LIST:
          editFilterList(button, filter, property);
          break;
        case External.CRM_PROPERTY_TYPE.COUNTRY:
          editFilterCountry(button, filter, property);
          break;
      }
    }
  }

  let editFilterTag = function (button, filter, property) {
    editFilterWithListOptions(
      button,
      filter,
      External.Website.crmTags,
      true,
      true,
      function (tag) {
        return tag.id;
      },
      function (tag) {
        return tag.label;
      }
    );
  };

  let editMemberGroup = function (button, filter, property) {
    editFilterWithListOptions(
      button,
      filter,
      External.Website.memberGroups,
      true,
      true,
      function (group) {
        return group.id;
      },
      function (group) {
        return group.name;
      }
    );
  };

  let editSubscriberList = function (button, filter, property) {
    getSubscriberLists(function (lists) {
      editFilterWithListOptions(
        button,
        filter,
        lists,
        true,
        true,
        function (list) {
          return list.id;
        },
        function (list) {
          return list.name;
        }
      );
    });
  };

  let editOrderStatusFilter = function (button, filter, property) {
    let statuses = ["PENDING", "SHIPPED", "COMPLETED", "CANCELED"];
    editFilterWithListOptions(
      button,
      filter,
      statuses,
      false,
      false,
      function (status) {
        return status;
      },
      function (status) {
        return M["ORDER_STATUS_" + status] || status;
      }
    );
  };

  let editFunnelFilter = function (button, filter, property) {
    let statuses = ["PENDING", "SHIPPED", "COMPLETED", "CANCELED"];
    editFilterWithListOptions(
      button,
      filter,
      External.Website.funnels || [],
      false,
      false,
      function (funnel) {
        return funnel.id;
      },
      function (funnel) {
        return funnel.name;
      }
    );
  };

  let editProductFilter = async function (button, filter, property) {
    let res = await makeWebsiteRequest({
      action: "listItemsByType",
      data: {
        type: External.Website.STORE_PAGE_ID,
        filter: id,
        showHidden: true
      }
    });
    let items = res.data || [];

    editFilterWithListOptions(
      button,
      filter,
      items,
      false,
      false,
      function (item) {
        return item.id;
      },
      function (item) {
        return item.name;
      }
    );
  };

  let getSubscriberLists = async function (onDone) {
    if (subscriberLists.length > 0) {
      onDone(subscriberLists);
      return;
    }

    let response = await makeWebsiteRequest({
      action: "getMarketingListsWithoutCount"
    });
    subscriberLists = response.data || [];
    onDone(subscriberLists);
  };

  let editFilterWithListOptions = function (button, filter, items, storeSelectedInFilterId, useLabelForValue, getItemId, getItemText) {
    let markup = [];
    let fields = ["is", "isNot"];

    let options = [{ label: "", value: "" }];
    for (let idx = 0; idx < items.length; ++idx) {
      let item = items[idx];
      options.push({
        value: External.escapeHtml(getItemId(item, idx)),
        label: External.escapeHtml(getItemText(item, idx))
      });
    }
    fields.forEach(function (func) {
      markup.push({
        func: func,
        name: funcNames[func],
        options: options
      });
    });

    let listOptionsInstance;

    External.Tools.showFloatingPopup({
      markup: '<div class="col20 f-left component-anchor"></div>',
      width: 300,
      target: button,
      customClass: "crm-filter-value",
      action: {
        onShow: function (popup, ui) {
          listOptionsInstance = new RadioWithSelect({
            target: ui.find(".component-anchor")[0],
            props: {
              data: markup,
              filter: filter,
              useLabelForValue: useLabelForValue,
              storeSelectedInFilterId: storeSelectedInFilterId,
              change: function () {
                onFilterChanged();
              }
            }
          });
        },
        onHide: function () {
          listOptionsInstance.$destroy();
        }
      }
    });
  };

  let editFilterWithSimpleInputs = function (button, filter, type, inputFields) {
    let markup = [],
      markupNoValue = [];

    inputFields.forEach(function (func) {
      markup.push({
        type: type,
        func: func,
        name: funcNames[func]
      });
    });

    let noValueFields = filter.isAlwaysDefined ? ["any"] : ["unknown", "any"];

    noValueFields.forEach(function (func) {
      markupNoValue.push({
        type: type,
        func: func,
        name: funcNames[func]
      });
    });

    let simpleInputsInstance;

    External.Tools.showFloatingPopup({
      markup: '<div class="col20 f-left component-anchor"></div>',
      width: 300,
      target: button,
      customClass: "crm-filter-value",
      action: {
        onShow: function (popup, ui) {
          simpleInputsInstance = new SimpleInputs({
            target: ui.find(".component-anchor")[0],
            props: {
              data: [markup, markupNoValue],
              filter: filter,
              change: function () {
                onFilterChanged();
              }
            }
          });
        },
        onHide: function () {
          simpleInputsInstance.$destroy();
          simpleInputsInstance = null;
        }
      }
    });
  };

  let editFilterBool = function (button, filter, property) {
    let markup = [];
    let noValueFields = filter.isAlwaysDefined ? ["isTrue", "isFalse", "any"] : ["isTrue", "isFalse", "unknown", "any"];

    noValueFields.forEach(function (func) {
      markup.push({
        func: func,
        name: funcNames[func]
      });
    });

    let radioNoInputInstance;

    External.Tools.showFloatingPopup({
      markup: '<div class="col20 f-left component-anchor"></div>',
      width: 300,
      target: button,
      customClass: "crm-filter-value",
      action: {
        onShow: function (popup, ui) {
          radioNoInputInstance = new RadioNoInput({
            target: ui.find(".component-anchor")[0],
            props: {
              data: markup,
              filter: filter,
              change: function () {
                onFilterChanged();
              }
            }
          });
        },
        onHide: function () {
          radioNoInputInstance.$destroy();
        }
      }
    });
  };

  let editFilterDate = function (button, filter, property) {
    let markup = [],
      markupRadioWithInput = [],
      markupRadioNoInput = [];
    let numberFields = ["moreThan", "exactly", "lessThan"];
    let dateFields = ["is", "after", "before"];
    let emptyFields = filter.isAlwaysDefined ? ["any"] : ["unknown", "any"];

    numberFields.forEach(function (func) {
      markup.push({
        type: "number",
        func: func,
        name: funcNames[func]
      });
    });

    dateFields.forEach(function (func) {
      markupRadioWithInput.push({
        type: "text",
        func: func,
        name: funcNames[func]
      });
    });

    emptyFields.forEach(function (func) {
      markupRadioNoInput.push({
        func: func,
        name: funcNames[func]
      });
    });

    let dateInstance;

    External.Tools.showFloatingPopup({
      markup: '<div class="col20 f-left component-anchor"></div>',
      width: 300,
      target: button,
      customClass: "crm-filter-value",
      action: {
        onShow: function (popup, ui) {
          dateInstance = new DateInput({
            target: ui.find(".component-anchor")[0],
            props: {
              data: [markup, markupRadioWithInput, markupRadioNoInput],
              filter: filter,
              change: function (f) {
                filter = f;
                onFilterChanged();
              }
            }
          });
        },
        onHide: function () {
          dateInstance.$destroy();
        }
      }
    });
  };

  let editFilterList = function (button, filter, property) {
    let items = property.value && property.value.length ? property.value : [];
    editFilterWithListOptions(
      button,
      filter,
      items,
      false,
      false,
      function (item) {
        return item;
      },
      function (item) {
        return item;
      }
    );
  };

  let refreshRenderedFilters = (triggerOnChangeEvent) => {
    if (isEmpty()) {
      renderFilters = [[]];
      if (triggerOnChangeEvent) {
        onChange();
      }
      return;
    }

    for (let i = 0; i < filters.length; i++) {
      renderFilters[i] = [];
      filters[i].forEach(function (filter) {
        renderFilter(filter, false, i);
      });
    }

    if (isLastFilterValid() && triggerOnChangeEvent) {
      onChange();
    }
  };

  let onFilterChangedDebounce = External.$.debounce(300, function () {
    refreshRenderedFilters(true);
  });

  let editFilterCountry = function (button, filter, property) {
    editFilterWithListOptions(
      button,
      filter,
      External.COUNTRY_CODES,
      false,
      false,
      function (countryCode, idx) {
        return countryCode;
      },
      function (countryCode, idx) {
        return External.COUNTRIES[idx] || countryCode;
      }
    );
  };

  let onDelete = (e, row, idx) => {
    e.stopPropagation();
    e.preventDefault();
    filters[row].splice(idx, 1);
    renderFilters[row].splice(idx, 1);
    filters = Object.assign([], filters, []);

    onFilterChanged();
  }

  let onClick = (e, row, idx) => {
    currentFilterRow = row;
    editFilterValue(External.$(e.target), filters[row][idx]);
  };

  let onDeleteRow = (e, row) => {
    filters.splice(row, 1);
    renderFilters.splice(row, 1);
    filters = Object.assign([], filters, []);

    onFilterChanged();
  };

  let toggleHideFilters = () => {
    hideFilters = !hideFilters;
  };

  afterUpdate(() => {
    showUpdate = id != null && !isEmpty();
  });

  onMount(() => {
    refreshRenderedFilters(false);
  });
</script>

<div class:none={hide}>
  <div class:none={hideFilters}>
    {#each renderFilters as filter, idx (filter)}
      <FilterRow onAddFilterClick={onAddFilterClick} onClick={onClick} onDelete={onDelete} renderFilters={filter} onDeleteRow={onDeleteRow} row={idx} />

      {#if renderFilters.length - 1 > idx}
        <div class="filter-divider c-left">{M.OR}</div>
      {/if}
    {/each}
    <a rel="action" class="hide-filters" class:none={!enableHide || !renderFilters || (renderFilters.length === 1 && renderFilters[0].length === 0)} on:click={toggleHideFilters}
      >{M.HIDE_FILTERS}</a>
    <a rel="action" class="filter-option blue save-segment" class:none={!showUpdate} on:click={onUpdateSegment}>
      {M.UPDATE_SEGMENT}
    </a>
    <a rel="action" class="filter-option blue save-segment" class:none={!enableSaveSegment || !showSaveSegment} on:click={onSaveSegment}>
      {M.SAVE_AS_NEW_SEGMENT}
    </a>
    {#if enableOR && !isListEmpty(filters)}
      <a on:click={(e) => addOr(e)} class="filter-option add-filter add-or-row c-left">
        <svg
          version="1.1"
          xmlns="http://www.w3.org/2000/svg"
          x="0px"
          y="0px"
          width="10px"
          height="10px"
          viewBox="0 0 357 357"
          style="enable-background:new 0 0 357 357;"
          xml:space="preserve">
          <path d="M357,204H204v153h-51V204H0v-51h153V0h51v153h153V204z" />
        </svg>
        {M.ADD_OR_CONDITION}
      </a>
    {/if}
  </div>
  <a rel="action" class="hide-filters" class:none={!hideFilters} on:click={toggleHideFilters}>{M.SHOW_FILTERS}</a>
</div>
